import React from "react";

import { FormStatic } from "@/components/Form";
import { PRIVACY_STATEMENT_LINK_AS } from "@/config";
import { CustomFieldType } from "@/service/Model";

enum FormFieldType {
  Input = "input",
  Checkbox = "checkbox",
  Radio = "radio",
  Static = "static",
  Select = "select",
}

interface Validation {
  required: boolean;
  regexp?: RegExp;
}

interface FormField {
  name: string;
  label?: string;
  type: FormFieldType | CustomFieldType;
  validation?: Validation;
  options?: Array<string>;
  element?: (
    termsConditionsLink?: string,
    privacyLink?: string,
    outroTitle?: string
  ) => JSX.Element;
}

interface FormFields {
  [key: string]: FormField;
}

export const nameRegex = /^[\p{L}\s,.'’‘/-]+$/u;
export const streetRegex = /^[\p{L}0-9\s,.'’‘/-]+$/u;
export const houseNumberRegex = /^[a-zA-Z0-9\s,.'/-]+$/;
export const postcodeRegex = /^\d{5}$/;
export const phoneRegex = /^[\d+.()/\s-]+$/;

// Alternatively there is a good validation library, but that would increase the package size.
// See here: https://github.com/validatorjs/validator.js/blob/master/src/lib/isEmail.js
export const emailRegex = /^(([^<>()[\]\\.,;:\s@?%/"]+(\.[^<>()[\]\\.,;:\s@?%/"]+)*)|.(".+"))@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/;

const getAvailableFormFields = (translation: any): FormFields => ({
  firstName: {
    name: "firstName",
    label: translation.registration.firstName,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: nameRegex,
    },
  },
  lastName: {
    name: "lastName",
    label: translation.registration.lastName,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: nameRegex,
    },
  },
  email: {
    name: "email",
    label: translation.registration.email,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: emailRegex,
    },
  },
  street: {
    name: "street",
    label: translation.registration.street,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: streetRegex,
    },
  },
  houseNumber: {
    name: "houseNumber",
    label: translation.registration.houseNumber,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: houseNumberRegex,
    },
  },
  postalcode: {
    name: "postalcode",
    label: translation.registration.postalcode,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: postcodeRegex,
    },
  },
  city: {
    name: "city",
    label: translation.registration.city,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: nameRegex,
    },
  },
  telephone: {
    name: "telephone",
    label: translation.registration.telephone,
    type: FormFieldType.Input,
    validation: {
      required: true,
      regexp: phoneRegex,
    },
  },
  advertisementOptIn: {
    name: "advertisementOptIn",
    label: translation.registration.advertisementOptInLabelAS(
      PRIVACY_STATEMENT_LINK_AS
    ),
    type: FormFieldType.Checkbox,
  },
  hansIdOptIn: {
    name: "hansIdOptIn",
    label: translation.registration.advertisementOptInLabelAS2(
      PRIVACY_STATEMENT_LINK_AS
    ),
    type: FormFieldType.Checkbox,
  },
  gender: {
    name: "gender",
    type: FormFieldType.Radio,
    options: ["male", "female"],
    validation: {
      required: true,
    },
  },
  infoText: {
    name: "infoText",
    type: FormFieldType.Static,
    element: (
      termsConditionsLink: string,
      privacyLink: string
    ): JSX.Element => (
      <FormStatic>
        <div
          dangerouslySetInnerHTML={{
            __html: translation.registration.termsAndConditions(
              termsConditionsLink
            ),
          }}
        />
        <div>
          <a href={privacyLink} target="_blank" rel="noreferrer">
            {translation.registration.privacy}
          </a>
        </div>
      </FormStatic>
    ),
  },
  outroTitle: {
    name: "outroTitle",
    type: FormFieldType.Static,

    element: (
      termsConditionsLink: string,
      privacyLink: string,
      outroTitle: string
    ): JSX.Element => (
      <FormStatic>
        <b data-qa="outro:title">
          {outroTitle ? outroTitle : translation.registration.startInfo}
        </b>
      </FormStatic>
    ),
  },

  prizeText: {
    name: "prizeText",
    type: FormFieldType.Static,
    element: (): JSX.Element => (
      <FormStatic>
        <b>{translation.registration.prizeInfo}</b>
      </FormStatic>
    ),
  },
});

const isValid = (field: FormField, value: string): boolean => {
  if (field.validation?.required && value.length === 0) {
    return false;
  }
  if (field.validation?.regexp) {
    return field.validation.regexp.test(value);
  }
  return true;
};

const getAllFieldsInArray = (array: any[]): FormField[] => {
  let formField: FormField[] = [];
  for (const item of array) {
    if (Array.isArray(item)) {
      // Attention: Recursion
      formField = [...formField, ...getAllFieldsInArray(item)];
    } else {
      formField.push(item);
    }
  }
  return formField;
};

export {
  FormField,
  FormFields,
  FormFieldType,
  getAllFieldsInArray,
  getAvailableFormFields,
  isValid,
  Validation,
};
